import React, { useState, useEffect } from "react";
import styles from "./RegisterUser.module.css";
import axiosInstance from "../../api/axiosConfig";
import { Link, useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const RegisterUser = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem("userRegistrationData");
    const initial = {
      dni: "",
      name: "",
      lastName: "",
      genre: "",
      birthdate: "",
      email: "",
      phone: "",
      country: "",
      department: "",
      province: "",
      district: "",
      address: "",
      postalCode: "",
      password: "",
      termsAccepted: false,
      termsAcceptedAt: null,
      dependents: [],
    };
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.timestamp && parsed.data) {
          if (new Date().getTime() - parsed.timestamp < 10 * 60 * 1000) {
            return { ...initial, ...parsed.data };
          }
          localStorage.removeItem("userRegistrationData");
        }
      } catch {}
    }
    return initial;
  });

  useEffect(() => {
    localStorage.setItem(
      "userRegistrationData",
      JSON.stringify({ data: formData, timestamp: new Date().getTime() })
    );
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      termsAccepted: e.target.checked,
      termsAcceptedAt: e.target.checked ? new Date() : null,
    }));
  };

  const addDependent = () => {
    setFormData((prev) => ({
      ...prev,
      dependents: [
        ...prev.dependents,
        { name: "", lastName: "", dni: "", birthdate: "", relationship: "" },
      ],
    }));
  };

  const removeDependent = (index) => {
    setFormData((prev) => ({
      ...prev,
      dependents: prev.dependents.filter((_, i) => i !== index),
    }));
  };

  const handleDependentChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...formData.dependents];
    updated[index] = { ...updated[index], [name]: value };
    setFormData((prev) => ({ ...prev, dependents: updated }));
  };

  const REQUIRED_FIELDS = [
    { key: "dni", label: "DNI" },
    { key: "name", label: "Nombres" },
    { key: "lastName", label: "Apellidos" },
    { key: "genre", label: "Género" },
    { key: "birthdate", label: "Fecha de nacimiento" },
    { key: "email", label: "Correo electrónico" },
    { key: "phone", label: "Teléfono" },
    { key: "country", label: "País" },
    { key: "department", label: "Departamento" },
    { key: "province", label: "Provincia" },
    { key: "district", label: "Distrito" },
    { key: "address", label: "Dirección" },
    { key: "postalCode", label: "Código postal" },
    { key: "password", label: "Contraseña" },
  ];

  const isFormValid =
    formData.termsAccepted &&
    REQUIRED_FIELDS.every((f) => String(formData[f.key] || "").trim() !== "") &&
    formData.password.length >= 8;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar campos requeridos
    const errors = {};
    REQUIRED_FIELDS.forEach(({ key, label }) => {
      if (!String(formData[key] || "").trim()) {
        errors[key] = `${label} es requerido`;
      }
    });
    if (formData.password && formData.password.length < 8) {
      errors.password = "La contraseña debe tener al menos 8 caracteres";
    }
    if (!formData.termsAccepted) {
      errors.terms = "Debes aceptar los términos y condiciones";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstKey = Object.keys(errors)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setFieldErrors({});
    setLoading(true);
    try {
      await axiosInstance.post("/api/register-user", formData);
      localStorage.removeItem("userRegistrationData");
      navigate("/registrado-exitosamente");
    } catch (error) {
      console.error("Error al enviar:", error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerInner}>
          <div className={styles.breadcrumb}>
            <Link to="/">Inicio</Link> / <span>Registro de usuario</span>
          </div>
          <div className={styles.headerRow}>
            <div className={styles.headerLeft}>
              <h1>Registro de usuario</h1>
              <p>Completá tus datos para crear tu cuenta en la plataforma.</p>
            </div>
            <span className={styles.loginHint}>
              ¿Ya tenés cuenta? <Link to="/login">Iniciá sesión →</Link>
            </span>
          </div>
        </div>
      </div>

      {/* ── Contenido ── */}
      <form onSubmit={handleSubmit}>
        <div className={styles.content}>

          {/* ── Card principal ── */}
          <div className={styles.card}>
            <p className={styles.stepLabel}>PASO ÚNICO</p>
            <h2 className={styles.cardTitle}>Datos personales y de acceso</h2>

            <div className={styles.formGrid}>
              {/* Fila 1 */}
              <div className={styles.field}>
                <label className={styles.label}>DNI*</label>
                <input className={`${styles.input} ${fieldErrors.dni ? styles.inputError : ""}`} name="dni" value={formData.dni} onChange={handleChange} placeholder="00000000" />
                {fieldErrors.dni && <span className={styles.errorMsg}>{fieldErrors.dni}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>NOMBRES*</label>
                <input className={`${styles.input} ${fieldErrors.name ? styles.inputError : ""}`} name="name" value={formData.name} onChange={handleChange} placeholder="Tus nombres" />
                {fieldErrors.name && <span className={styles.errorMsg}>{fieldErrors.name}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>APELLIDOS*</label>
                <input className={`${styles.input} ${fieldErrors.lastName ? styles.inputError : ""}`} name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Tus apellidos" />
                {fieldErrors.lastName && <span className={styles.errorMsg}>{fieldErrors.lastName}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>GÉNERO*</label>
                <select className={`${styles.select} ${fieldErrors.genre ? styles.inputError : ""}`} name="genre" value={formData.genre} onChange={handleChange}>
                  <option value=""></option>
                  <option value="Masculino">Masculino</option>
                  <option value="Femenino">Femenino</option>
                  <option value="Otro">Otro</option>
                </select>
                {fieldErrors.genre && <span className={styles.errorMsg}>{fieldErrors.genre}</span>}
              </div>

              {/* Fila 2 */}
              <div className={styles.field}>
                <label className={styles.label}>FECHA DE NACIMIENTO*</label>
                <input className={`${styles.input} ${fieldErrors.birthdate ? styles.inputError : ""}`} type="date" name="birthdate" value={formData.birthdate} onChange={handleChange} />
                {fieldErrors.birthdate && <span className={styles.errorMsg}>{fieldErrors.birthdate}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>CORREO ELECTRÓNICO*</label>
                <input className={`${styles.input} ${fieldErrors.email ? styles.inputError : ""}`} type="email" name="email" value={formData.email} onChange={handleChange} placeholder="tucorreo@email.com" />
                {fieldErrors.email && <span className={styles.errorMsg}>{fieldErrors.email}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>TELÉFONO*</label>
                <input className={`${styles.input} ${fieldErrors.phone ? styles.inputError : ""}`} type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+51 999 999 999" />
                {fieldErrors.phone && <span className={styles.errorMsg}>{fieldErrors.phone}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>PAÍS*</label>
                <input className={`${styles.input} ${fieldErrors.country ? styles.inputError : ""}`} name="country" value={formData.country} onChange={handleChange} placeholder="Perú" />
                {fieldErrors.country && <span className={styles.errorMsg}>{fieldErrors.country}</span>}
              </div>

              {/* Fila 3 */}
              <div className={styles.field}>
                <label className={styles.label}>DEPARTAMENTO*</label>
                <input className={`${styles.input} ${fieldErrors.department ? styles.inputError : ""}`} name="department" value={formData.department} onChange={handleChange} placeholder="Lima" />
                {fieldErrors.department && <span className={styles.errorMsg}>{fieldErrors.department}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>PROVINCIA*</label>
                <input className={`${styles.input} ${fieldErrors.province ? styles.inputError : ""}`} name="province" value={formData.province} onChange={handleChange} placeholder="Lima" />
                {fieldErrors.province && <span className={styles.errorMsg}>{fieldErrors.province}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>DISTRITO*</label>
                <input className={`${styles.input} ${fieldErrors.district ? styles.inputError : ""}`} name="district" value={formData.district} onChange={handleChange} placeholder="Miraflores" />
                {fieldErrors.district && <span className={styles.errorMsg}>{fieldErrors.district}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>DIRECCIÓN*</label>
                <input className={`${styles.input} ${fieldErrors.address ? styles.inputError : ""}`} name="address" value={formData.address} onChange={handleChange} placeholder="Av. Ejemplo 123" />
                {fieldErrors.address && <span className={styles.errorMsg}>{fieldErrors.address}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>CÓDIGO POSTAL*</label>
                <input className={`${styles.input} ${fieldErrors.postalCode ? styles.inputError : ""}`} name="postalCode" value={formData.postalCode} onChange={handleChange} placeholder="Ej. 15001" />
                {fieldErrors.postalCode && <span className={styles.errorMsg}>{fieldErrors.postalCode}</span>}
              </div>

              {/* Fila 4 - contraseña sola */}
              <div className={styles.field}>
                <label className={styles.label}>CONTRASEÑA*</label>
                <div className={styles.passwordWrap}>
                  <input
                    className={styles.input}
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Mínimo 8 caracteres"
                    minLength={8}
                  />
                  <button type="button" className={styles.eyeBtn} onClick={() => setShowPassword((v) => !v)}>
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
                {fieldErrors.password && <span className={styles.errorMsg}>{fieldErrors.password}</span>}
              </div>
              <div /><div /><div />
            </div>
          </div>

          {/* ── Card dependientes ── */}
          <div className={styles.card}>
            <div className={styles.cardTitleRow}>
              <div>
                <p className={styles.stepLabel}>OPCIONAL</p>
                <h2 className={styles.cardTitle}>Dependientes</h2>
              </div>
              <button type="button" className={styles.btnAddDependent} onClick={addDependent}>
                + AGREGAR DEPENDIENTE
              </button>
            </div>

            {formData.dependents.length === 0 ? (
              <div className={styles.emptyDependents}>
                No has agregado dependientes. Podés añadir familiares para gestionar sus citas desde tu cuenta.
              </div>
            ) : (
              formData.dependents.map((dep, i) => (
                <div key={i} className={styles.dependentCard}>
                  <div className={styles.dependentHeader}>
                    <div className={styles.dependentTitle}>
                      <span className={styles.dependentNum}>{i + 1}</span>
                      Dependiente {i + 1}
                    </div>
                    <button type="button" className={styles.btnRemove} onClick={() => removeDependent(i)}>
                      ✕ Quitar
                    </button>
                  </div>
                  <div className={styles.dependentGrid}>
                    <div className={styles.field}>
                      <label className={styles.label}>NOMBRES*</label>
                      <input className={styles.input} name="name" value={dep.name} onChange={(e) => handleDependentChange(i, e)} placeholder="Nombres" required />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label}>APELLIDOS*</label>
                      <input className={styles.input} name="lastName" value={dep.lastName} onChange={(e) => handleDependentChange(i, e)} placeholder="Apellidos" required />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label}>DNI</label>
                      <input className={styles.input} name="dni" value={dep.dni} onChange={(e) => handleDependentChange(i, e)} placeholder="00000000" />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label}>FECHA DE NACIMIENTO</label>
                      <input className={styles.input} type="date" name="birthdate" value={dep.birthdate} onChange={(e) => handleDependentChange(i, e)} />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label}>PARENTESCO*</label>
                      <input className={styles.input} name="relationship" value={dep.relationship} onChange={(e) => handleDependentChange(i, e)} placeholder="Ej. Hijo/a" required />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── Card términos + submit ── */}
          <div className={styles.card}>
            <div className={styles.footerCard}>
              <label className={styles.checkRow}>
                <input type="checkbox" checked={formData.termsAccepted} onChange={handleCheckboxChange} />
                Acepto los <Link to="/terminos-condiciones">términos y condiciones</Link> de Vitalclic *
              </label>
              {fieldErrors.terms && <span className={styles.errorMsg}>{fieldErrors.terms}</span>}
              <button type="submit" className={styles.btnSubmit} disabled={loading || !isFormValid}>
                {loading
                  ? <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />
                  : "REGISTRARSE"}
              </button>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default RegisterUser;
